import {
  BackrsFormField,
  ContentNugget,
  NuggetVariant,
  PriorityPlaylist,
} from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { BrandDatePicker } from '@backrs_ui/grow-ui/src/pages/SatActBooster/components/BrandDatePicker';
import { ContentSearchAutocompleteResult } from '@backrs_ui/grow-ui/src/redux/contentSearch/contentSearch.api';
import { AutocompleteResult } from '@backrs_ui/grow-ui/src/redux/onboarding/onboarding.api';
import {
  TaskContentCode,
  PriorityTaskType,
  PriorityTask,
} from '@backrs_ui/grow-ui/src/redux/priorities/priorities.types';

import { UseFormReturn } from 'react-hook-form';

import {
  transformTaskContentsToNuggets,
  determineTaskAutocompleteContentCode,
} from '../utils/tasks.utils';

const STRINGS = appStrings.modules.priorities.taskDialog;

const determineTaskTypesToShow = (taskTypes: PriorityTaskType[], hasNuggetLink: boolean) => {
  if (hasNuggetLink) {
    return taskTypes;
  }

  return taskTypes.filter((type) => type.is_user_selectable);
};

const determineExistingContent = (
  taskToEdit: PriorityTask | null,
  nuggetToAdd: ContentNugget | null,
): ContentNugget | null => {
  // remove the [0] here & refactor if we want to allow multiple nuggets to be associated with a task
  const taskNugget = taskToEdit ? transformTaskContentsToNuggets(taskToEdit)[0] : nuggetToAdd;

  return taskNugget || null;
};

const determineIsThereAssociatedContent = (
  nuggetToAdd: ContentNugget | null,
  taskToEdit: PriorityTask | null,
  preloadedTaskInfo?: Partial<PriorityTask>,
): boolean => {
  if (nuggetToAdd) {
    return true;
  }

  if (taskToEdit) {
    // check and see if there is any associated content
    return transformTaskContentsToNuggets(taskToEdit).length > 0;
  }

  if (preloadedTaskInfo) {
    // Check if preloaded task has any content arrays with items
    const hasContent = !!(
      (preloadedTaskInfo.contentNuggets && preloadedTaskInfo.contentNuggets.length > 0) ||
      (preloadedTaskInfo.scholarships && preloadedTaskInfo.scholarships.length > 0) ||
      (preloadedTaskInfo.internships && preloadedTaskInfo.internships.length > 0) ||
      (preloadedTaskInfo.careerPaths && preloadedTaskInfo.careerPaths.length > 0) ||
      (preloadedTaskInfo.profiles && preloadedTaskInfo.profiles.length > 0) ||
      (preloadedTaskInfo.externalProfiles && preloadedTaskInfo.externalProfiles.length > 0) ||
      (preloadedTaskInfo.academicProfiles && preloadedTaskInfo.academicProfiles.length > 0) ||
      (preloadedTaskInfo.employers && preloadedTaskInfo.employers.length > 0) ||
      (preloadedTaskInfo.competencies && preloadedTaskInfo.competencies.length > 0) ||
      (preloadedTaskInfo.trackAssessmentResults &&
        preloadedTaskInfo.trackAssessmentResults.length > 0) ||
      (preloadedTaskInfo.trackAssessmentUnlocks &&
        preloadedTaskInfo.trackAssessmentUnlocks.length > 0) ||
      (preloadedTaskInfo.educationalInvestments &&
        preloadedTaskInfo.educationalInvestments.length > 0)
    );

    return hasContent;
  }

  return false;
};

type GetTaskDialogFormFieldsProps = Readonly<{
  playlists: PriorityPlaylist[];
  taskTypes: PriorityTaskType[];
  nuggetToAdd: ContentNugget | null;
  nuggetTaskTypeId: PriorityTaskType | undefined;
  contentCode: TaskContentCode | undefined;
  taskToEdit?: PriorityTask | null;
  preloadedTaskInfo?: Partial<PriorityTask>;
  handleOpeningPlaylistDialog: () => void;
  autoCompleteOptions: AutocompleteResult[];
  setAutoCompleteOptions: React.Dispatch<React.SetStateAction<AutocompleteResult[]>>;
  handleInputChange: (input: string) => void;
  formMethods?: UseFormReturn;
}>;

export const taskDialogFormConfig = ({
  playlists,
  taskTypes,
  nuggetToAdd,
  nuggetTaskTypeId,
  contentCode,
  taskToEdit,
  preloadedTaskInfo,
  handleOpeningPlaylistDialog,
  autoCompleteOptions,
  setAutoCompleteOptions,
  handleInputChange,
  formMethods,
}: GetTaskDialogFormFieldsProps) => {
  // Helper function to format date for date picker
  const formatDateForPicker = (dateString: string | null | undefined) => {
    if (!dateString) return '';

    return new Date(dateString).toISOString().split('T')[0];
  };

  const taskTypesToShow = determineTaskTypesToShow(taskTypes, !!nuggetToAdd);

  const isThereAssociatedContent = determineIsThereAssociatedContent(
    nuggetToAdd,
    taskToEdit || null,
    preloadedTaskInfo,
  );

  const existingContentSearchOptions: ContentSearchAutocompleteResult | undefined =
    autoCompleteOptions.find((option) => option.endpoint === '/content-search');

  const fields: BackrsFormField[] = [
    {
      id: 'title',
      name: 'title',
      type: 'text',
      label: 'Task Title*',
      placeholder: 'e.g. Research colleges',
      labelPlacement: 'left',
      autoComplete: 'title',
      rules: {
        required: true,
        maxLength: 50,
      },
      autoFocus: true,
      defaultValue: taskToEdit?.title || '',
      showMaxLenCharCount: true,
    },
    {
      id: 'description',
      name: 'description',
      type: 'textarea',
      placeholder: 'Add notes for your task',
      label: 'Notes',
      labelPlacement: 'left',
      autoComplete: 'description',
      divider: true,
      defaultValue: taskToEdit?.description || '',
    },
    {
      id: 'playlist_id',
      name: 'playlist_id',
      type: 'number',
      label: 'Add to Playbook',
      labelPlacement: 'left',
      autoComplete: 'playlist_id',
      placeholder: 'Select a playbook',
      defaultValue: taskToEdit?.playlist_id?.toString() || '',
      selectOptions: {
        type: 'SINGLE',

        renderAsSelect: true,
        options: [
          {
            label: 'Create a playbook',
            value: 'create-playbook',
            onClick: () => handleOpeningPlaylistDialog(),
          },
          ...playlists.map((playlist) => ({
            label: playlist.title,
            value: playlist.id.toString(),
          })),
        ],
      },

      divider: isThereAssociatedContent,
    },
    {
      id: 'type_code',
      name: 'type_code',
      type: 'string',
      label: 'Task Type',
      labelPlacement: 'left',
      autoComplete: 'type_code',
      placeholder: 'Select a task type',
      hidden: isThereAssociatedContent,
      defaultValue: nuggetTaskTypeId?.code || '',
      selectOptions: {
        type: 'SINGLE',
        renderAsSelect: true,
        options: taskTypesToShow.map((type) => ({
          label: type.display_name,
          value: type.code,
        })),
      },
      divider: true,
    },

    {
      id: 'due_date',
      name: 'due_date',
      type: 'date',
      label: 'Due Date',
      labelPlacement: 'left',
      autoComplete: 'due_date',
      defaultValue: formatDateForPicker(taskToEdit?.due_date),
      renderControl: (controllerField, error) => (
        <BrandDatePicker
          id="task-dialog-due-date"
          value={controllerField.value || null}
          onChange={(next) => controllerField.onChange(next ?? '')}
          onBlur={controllerField.onBlur}
          placeholder={STRINGS.dueDatePlaceholder}
          error={!!error}
          helperText={error}
          fullWidth
        />
      ),
    },

    {
      id: 'priority',
      name: 'priority',
      type: 'number',
      label: 'Priority Level',
      labelPlacement: 'left',
      autoComplete: 'priority',
      placeholder: 'Select Priority',
      defaultValue: taskToEdit?.priority?.toString() || '',
      divider: true,
      selectOptions: {
        type: 'SINGLE',
        renderAsSelect: true,
        options: [
          { label: 'Low', value: '1' },
          { label: 'Medium', value: '2' },
          { label: 'High', value: '3' },
        ],
      },
    },
    {
      id: 'content_id',
      name: 'content_id',
      type: 'text',
      label: 'Search Content',
      labelPlacement: 'left',
      placeholder: 'Search for content',
      defaultValue: (() => {
        const existingContent = determineExistingContent(taskToEdit || null, nuggetToAdd);

        return existingContent?.id?.toString() || '';
      })(),
      disabledBy: {
        field: 'content_url',
        values: [],
        disableWhenTruthy: true,
      },
      autoCompleteEndpoint: '/content-search',
      selectOptions: {
        type: 'SINGLE',
        renderAsSelect: true,
        options: [
          ...(() => {
            const existingContent = determineExistingContent(taskToEdit || null, nuggetToAdd);

            if (existingContent) {
              const option = {
                label: existingContent.title,
                value: existingContent.id.toString(),
                variant: existingContent.variant,
                title: existingContent.title,
                hasRelatedCollegeInfo: !!existingContent?.college_info?.id,
              };

              return [option];
            }

            return [];
          })(),
          ...(existingContentSearchOptions?.values || []).map((value) => ({
            label: value.label,
            value: value.value,
            variant: value.variant,
            title: value.title,
            hasRelatedCollegeInfo: value?.hasRelatedCollegeInfo,
          })),
        ],
      },
      onInputChange: (input: string) => handleInputChange(input),
      afterOnChange: (
        newValue: {
          label: string;
          value: string;
          variant?: NuggetVariant;
          title?: string;
          hasRelatedCollegeInfo?: boolean;
        } | null,
      ) => {
        if (formMethods && newValue) {
          if (!formMethods.getValues('title')) {
            formMethods.setValue('title', newValue.title, {
              shouldDirty: true,
              shouldValidate: true,
            });
          }

          const contentCode = determineTaskAutocompleteContentCode(
            newValue.variant,
            newValue.hasRelatedCollegeInfo,
          );

          formMethods.setValue('content_code', contentCode);
        } else {
          formMethods?.setValue('content_code', '');
        }
      },
      onOptionSelected: () => {
        setAutoCompleteOptions((prev) =>
          prev.map((opt) =>
            opt.endpoint === '/content-search' ? { ...opt, searchTerm: '', values: [] } : opt,
          ),
        );
      },
    } as BackrsFormField,
    {
      id: 'content_code',
      name: 'content_code',
      type: 'text',
      defaultValue: contentCode || '',
      hidden: true,
    },
    {
      id: 'content_url',
      name: 'content_url',
      type: 'text',
      label: 'Link',
      labelPlacement: 'left',
      autoComplete: 'content_url',
      placeholder: 'Add a link to your task',
      defaultValue: taskToEdit?.content_url || '',
      disabledBy: {
        field: 'content_id',
        values: [],
        disableWhenTruthy: true,
      },
    } as BackrsFormField,
    {
      id: 'tags',
      name: 'tags',
      type: 'textarea',
      label: 'Tags',
      labelPlacement: 'left',
      placeholder: 'Add 1-10 keywords to help you find your task',
      manualFilterOptions: true,
      defaultValue: '',
      selectOptions: {
        type: 'MULTIPLE',
        renderAsSelect: true,
        chipLayout: 'inline',
        options: (() => {
          const editTags =
            taskToEdit?.tags?.map((tag) => ({
              label: tag.title,
              value: tag.title,
            })) || [];

          const preloadedTags =
            preloadedTaskInfo?.tags?.map((tag) => ({
              label: typeof tag === 'string' ? tag : tag.title,
              value: typeof tag === 'string' ? tag : tag.title,
            })) || [];

          return editTags.length > 0 ? editTags : preloadedTags;
        })(),
      },
    },
  ];

  return fields;
};
